/**
 * The Azure block-blob REST verbs, spoken directly by the browser (UPL-1).
 *
 * This is deliberately **not** part of `api.ts`. Nothing here is this system's
 * API: it does not go through `VITE_API_BASE_URL`, it carries no bearer token,
 * it exchanges XML rather than JSON, and it is authorized by the short-lived,
 * blob-scoped SAS the API hands out (SEC-3). Bytes never pass through the
 * Functions app — that is the whole point of the upload pipeline.
 *
 * **The SAS in `sasUrl` is a credential.** Never store it, never log it, never
 * put it in the DOM.
 */

/** A refusal from blob storage, with the status the caller has to branch on. */
export class BlobError extends Error {
  /** `0` is network-level: DNS, CORS, offline. */
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'BlobError';
    this.status = status;
  }
}

/** Azure's ceiling on blocks in one block blob, mirrored from `apps/api/src/shared/uploads.ts`. */
const MAX_BLOCKS = 50_000;

/** Wide enough for `MAX_BLOCKS - 1`, which is what makes every id the same length. */
const INDEX_WIDTH = String(MAX_BLOCKS - 1).length;

/**
 * The block id for a block's position in the file.
 *
 * Azure requires every block id within one blob to be **the same length** before
 * base64, and refuses the block list otherwise. Zero-padding the index makes
 * that true by construction rather than by luck, and keeps the ids sorting in
 * the order the blocks go back together.
 */
export function blockIdFor(index: number): string {
  return btoa(`block-${String(index).padStart(INDEX_WIDTH, '0')}`);
}

export interface BlobRequestOptions {
  signal?: AbortSignal;
}

export interface StageBlockOptions extends BlobRequestOptions {
  /** Cumulative bytes of *this block* that have left the browser. */
  onProgress?: (sent: number) => void;
}

/**
 * Put Block — stage one chunk against the blob the SAS addresses.
 *
 * **This is the one request in the app made with `XMLHttpRequest` rather than
 * `fetch`**, and the reason is UPL-3: this system moves body-worn-camera video
 * and phone extractions, so a block is 8–100 MiB and a file is measured in
 * gigabytes. `fetch` reports nothing until a request completes, which would
 * leave a progress bar motionless for minutes at a time — indistinguishable, to
 * the person watching it, from a hang. `xhr.upload` is the only web API that
 * reports bytes as they leave.
 *
 * Staging is idempotent: re-staging a block id overwrites it, which is what
 * makes a retry safe (UPL-2).
 */
export async function stageBlock(
  sasUrl: string,
  blockId: string,
  body: Blob,
  options: StageBlockOptions,
): Promise<void> {
  const url = withQuery(sasUrl, `comp=block&blockid=${encodeURIComponent(blockId)}`);

  /**
   * Read the slice before sending it, rather than handing `xhr.send` the `Blob`.
   *
   * A real browser streams a `Blob` straight off disk, which is strictly
   * better — but neither jsdom nor MSW's XHR interceptor carries one through,
   * and they turn the body into the string `"undefined"` without erroring. A
   * transport that only works outside the test suite is a transport nobody can
   * test, and this is a slice rather than the file: the cost is one block per
   * in-flight request, so 24 MiB at the 8 MiB block size and the concurrency
   * the engine uses.
   */
  const payload = await body.arrayBuffer();

  return new Promise((resolve, reject) => {
    if (options.signal?.aborted === true) {
      reject(abortError());
      return;
    }

    const request = new XMLHttpRequest();
    request.open('PUT', url, true);

    const onAbort = () => {
      request.abort();
    };
    options.signal?.addEventListener('abort', onAbort);

    const settle = (finish: () => void) => {
      options.signal?.removeEventListener('abort', onAbort);
      finish();
    };

    if (options.onProgress !== undefined) {
      const report = options.onProgress;
      request.upload.addEventListener('progress', (event) => {
        report(event.loaded);
      });
    }

    request.addEventListener('load', () => {
      settle(() => {
        if (request.status >= 200 && request.status < 300) {
          resolve();
        } else {
          reject(
            new BlobError(request.status, `Staging a block failed (${String(request.status)}).`),
          );
        }
      });
    });

    request.addEventListener('error', () => {
      settle(() => {
        reject(new BlobError(0, 'Could not reach storage to stage a block.'));
      });
    });

    request.addEventListener('abort', () => {
      settle(() => {
        reject(abortError());
      });
    });

    request.send(payload);
  });
}

/**
 * Put Block List — assemble the staged blocks into the blob, in this order.
 *
 * `<Latest>` rather than `<Uncommitted>` so a block that was staged twice
 * resolves to whichever copy arrived last, which is exactly what a retry wants.
 *
 * **The blob's content type is deliberately not set here.** The browser's guess
 * travels to `POST /upload-sessions/{sid}/finalize` instead, where the API
 * derives the file's `kind` from it (FIL-4). Sending it as `x-ms-blob-content-type`
 * would add one more header to every agency storage account's CORS allow-list
 * for no visible gain: the download SAS already forces `Content-Disposition:
 * attachment`, so the blob's own type never decides anything.
 */
export async function commitBlockList(
  sasUrl: string,
  blockIds: readonly string[],
  options: BlobRequestOptions,
): Promise<void> {
  const body =
    `<?xml version="1.0" encoding="utf-8"?><BlockList>` +
    blockIds.map((id) => `<Latest>${id}</Latest>`).join('') +
    `</BlockList>`;

  const response = await send(withQuery(sasUrl, 'comp=blocklist'), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/xml' },
    body,
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });

  if (!response.ok) {
    throw new BlobError(
      response.status,
      `Committing the block list failed (${String(response.status)}).`,
    );
  }
}

/**
 * Get Block List — what already crossed the wire, for a resume (UPL-2).
 *
 * The API deliberately does not answer this: it is not in the byte path, and
 * proxying a block list would put it there. The `r` in the upload SAS's `rcw`
 * permissions exists for exactly this call.
 *
 * `blocklisttype=all` on purpose. A resume has to skip **both** halves: a
 * committed block is already on the blob, and an uncommitted one has already
 * been paid for in bandwidth. Re-sending either is the multi-gigabyte cost this
 * whole query exists to avoid.
 */
export async function listStagedBlocks(
  sasUrl: string,
  options: BlobRequestOptions,
): Promise<Set<string>> {
  const response = await send(withQuery(sasUrl, 'comp=blocklist&blocklisttype=all'), {
    method: 'GET',
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });

  // A blob nothing has been staged against may not exist yet, and Azure's
  // answer for that has varied by service version. "Nothing staged" is the safe
  // reading either way: the worst it costs is re-staging blocks that had
  // already landed, and the alternative is a resume that fails on its first act.
  if (response.status === 404) return new Set();

  if (!response.ok) {
    throw new BlobError(
      response.status,
      `Reading the block list failed (${String(response.status)}).`,
    );
  }

  const document = new DOMParser().parseFromString(await response.text(), 'application/xml');

  // Every `Name` in the document is a block id, under `CommittedBlocks` or
  // `UncommittedBlocks` alike — and both are skipped, so the two do not need
  // telling apart here.
  return new Set(
    [...document.querySelectorAll('Name')]
      .map((element) => element.textContent ?? '')
      .filter((id) => id !== ''),
  );
}

/**
 * Append query parameters to a URL that already carries a SAS.
 *
 * **String concatenation, deliberately — not `new URL(...).searchParams`.** That
 * API parses the query and re-serialises the whole of it as
 * `application/x-www-form-urlencoded`, which re-encodes every value in the
 * signed token on the way past. The signature is computed over the decoded
 * values so it usually survives, but "usually" is the wrong standard for a
 * credential: a token this mangles fails as an opaque 403, a long way from the
 * line that mangled it. Appending touches nothing that was already there.
 */
function withQuery(sasUrl: string, query: string): string {
  return `${sasUrl}${sasUrl.includes('?') ? '&' : '?'}${query}`;
}

async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (cause) {
    if (cause instanceof Error && cause.name === 'AbortError') throw cause;
    throw new BlobError(0, 'Could not reach storage.');
  }
}

function abortError(): DOMException {
  return new DOMException('The upload was cancelled.', 'AbortError');
}
